import { unitTypes, infusionDosingTypes, dosageForms } from '../../config/medication';

function validateMedications(caseConf) {
  const errors = [];
  const warnings = [];

  // IV Requirements
  const hasInfusionMed = (caseConf.dosageForms || []).reduce((accumulator, df) => {
    return accumulator || (df.enabledRoutes && !!df.enabledRoutes.find(
      route => route.key === 'IV_IO_INFUSION'
    ));
  }, false);
  if (hasInfusionMed && !caseConf.iv.enabled) {
    errors.push(ivError(
      'IV must be enabled when infusion medications are used', 'enabled'
    ));
  }

  const hasPushMed = (caseConf.dosageForms || []).reduce((accumulator, df) => {
    return accumulator || (df.enabledRoutes && !!df.enabledRoutes.find(
      route => route.key === 'IV_IO_PUSH'
    ));
  }, false);
  if (hasPushMed && !caseConf.iv.enabled) {
    warnings.push(ivError(
      'IV must be enabled to administer medications with IV/IO Push', 'enabled'
    ));
  }

  // Number of infusion medications
  let infusionMedCount = 0;
  for (const dosage of caseConf.dosageForms || []) {
    if ((dosage.enabledRoutes || []).find(route => route.key === 'IV_IO_INFUSION')) {
      infusionMedCount++;
    }
  }
  if (infusionMedCount > 31) {
    errors.push(medicationsError(
      'There can only be up to 31 medications with an IV/IO Infusion route', 'infusion-count'
    ));
  }

  for (const dosage of caseConf.dosageForms || []) {
    // Administering unit
    if (!dosage.administeringUnits || dosage.administeringUnits.length < 1) {
      errors.push(dosageError(
        'Dosage form must have administering unit selected', dosage.id, 'administeringUnit'
      ));
    } else if (!dosage.concentration.numeratorValue || dosage.concentration.numeratorValue <= 0) {
      errors.push(dosageError(
        'Dosage concentration must have non-zero values', dosage.id, 'numeratorValue'
      ));
    } else if (
      (dosage.dosageFormType === 'SOLUTION' || dosage.dosageFormType === 'SUSPENSION') &&
      (!dosage.concentration.denominatorValue || dosage.concentration.denominatorValue <= 0)
    ) {
      errors.push(dosageError(
        'Dosage concentration must have non-zero values', dosage.id, 'denominatorValue'
      ));
    } else {
      for (const adminUnit of dosage.administeringUnits) {
        const unitType = unitTypes.find(ut => ut.key === adminUnit);
        if (unitType && unitType.category) {
          const numeratorUnitType = unitTypes.find(ut => ut.key ===  dosage.concentration.numeratorUnits);
          const dosageForm = dosageForms.find(df => df.key === dosage.dosageFormType);
          let denominatorUnitType;
          if (dosageForm.unitType2) {
            denominatorUnitType = unitTypes.find(ut => ut.key === dosageForm.unitType2);
          } else {
            denominatorUnitType = unitTypes.find(ut => ut.key === dosage.concentration.denominatorUnits);
          }
          if (unitType.category !== numeratorUnitType.category && unitType.category !== denominatorUnitType.category) {
            errors.push(dosageError(
              'Administering unit does not match concentration', dosage.id, 'administeringUnit'
            ));
          }
        }
        if (unitType && !unitType.category) {
          const dosageForm = dosageForms.find(df => df.key === dosage.dosageFormType);
          if (unitType.key !== dosageForm.unitType2) {
            errors.push(dosageError(
              'Administering unit does not match concentration', dosage.id, 'administeringUnit'
            ));
          }
        }
      }
    }

    // Routes
    if (!dosage.enabledRoutes || !dosage.enabledRoutes.length) {
      errors.push(dosageError(
        'Dosage form must have at least one route enabled', dosage.id, 'no-routes'
      ));
    }

    for (const route of dosage.enabledRoutes || []) {
      if (route.key === 'IV_IO_PUSH') {
        // Timeframe
        if (!route.options.pushTimeframes || route.options.pushTimeframes.length === 0) {
          errors.push(dosageError(
            'Dosage form with IV/IO Push route must have at least one time frame selected', dosage.id, 'pushTimeframes'
          ));
        }
      }

      if (route.key === 'IV_IO_INFUSION') {
        // Dosing types
        if (!route.options.infusionDosingTypes || route.options.infusionDosingTypes.length === 0) {
          errors.push(dosageError(
            'Dosage form with IV/IO Infusion route must have at least one dosing selected', dosage.id, 'infusionDosingTypes'
          ));
        }

        // Dosing type category must be compatible with concentration numerator units
        for (const dosingKey of route.options.infusionDosingTypes || []) {
          const dosingType = infusionDosingTypes.find(dt => dt.key == dosingKey);
          if (dosingType.category === 'VOLUME') {
            const unitType = unitTypes.find(ut => ut.key === dosage.concentration.denominatorUnits);
            if (unitType && unitType.category !== dosingType.category) {
              errors.push(dosageError(
                'Dosing type uses volume, but concentration does not', dosage.id, 'infusionDosingTypes'
              ));
            }
          } else {
            const unitType = unitTypes.find(ut => ut.key === dosage.concentration.numeratorUnits);
            if (unitType && unitType.category !== dosingType.category) {
              errors.push(dosageError(
                `Dosing types must start with a ${unitType.category}`, dosage.id, 'infusionDosingTypes'
              ));
            }
          }
        }

        // Bag volumes
        if (!route.options.infusionBagVolumes || route.options.infusionBagVolumes.length === 0) {
          errors.push(dosageError(
            'Dosage form with IV/IO Infusion route must have at least one volume selected', dosage.id, 'infusionBagVolumes'
          ));
        }

        // Formularies
        for (let i = 0; i < (route.options.formularies || []).length; i++) {
          const formulary = route.options.formularies[i];
          if (formulary.dose <= 0) {
            errors.push(dosageError(
              'Formulary dose must be greater than 0', dosage.id, `formulary[${i}].dose`
            ));
          }
          if (!route.options.infusionDosingTypes.includes(formulary.dosing)) {
            errors.push(dosageError(
              'Formulary dosing must belong to the dosage form\'s selected dosings', dosage.id, `formulary[${i}].dosing`
            ));
          }
          if (!route.options.infusionBagVolumes.includes(formulary.volume)) {
            errors.push(dosageError(
              'Formulary volume must belong to the dosage form\'s selected volumes', dosage.id, `formulary[${i}].volume`
            ));
          }
        }
      }
    }
  }


  return {
    errors,
    warnings,
  };
}

function medicationsError(message, id) {
  return {
    message,
    category: 'medications',
    field: 'medications',
    id,
    metadata: {
      scope: 'control',
    },
  };
}

function dosageError(message, dosageFormId, propertyName) {
  return {
    message,
    category: 'medications',
    field: 'dosageForms',
    id: dosageFormId,
    metadata: {
      scope: 'control',
      propertyName,
    },
  };
}

function ivError(message, id) {
  return {
    message,
    category: 'ivs',
    field: 'iv',
    id,
    metadata: {
      scope: 'control',
    },
  };
}

export default validateMedications;
